import { useEffect, useRef } from 'react';
import { Box, Chip, CircularProgress, Container, Grid, Stack, Typography } from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import {
  OtaFooter,
  OtaPopularCities,
  OtaPropertyCard,
  OtaSearchBar,
  OtaValueProps,
  type OtaPropertySummary,
  type OtaSearchValue,
} from '@ota/design-system';
import { useNavigate } from 'react-router-dom';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { fetchPropertySuggestions, fetchPublicStats, searchProperties } from '../api';
import { PublicHeader } from '../components/PublicHeader';

// Page size for the "all hotels" infinite-scroll section below. /properties/public/search
// already supports page+size (see PropertyController#publicSearch) — this just fetches one
// page at a time instead of a single fixed batch, and appends as the user scrolls.
const PAGE_SIZE = 12;

export function HomePage() {
  const navigate = useNavigate();
  const featured = useInfiniteQuery({
    queryKey: ['all-properties'],
    queryFn: ({ pageParam }) => searchProperties({ size: PAGE_SIZE, page: pageParam }),
    initialPageParam: 0,
    getNextPageParam: (lastPage) =>
      lastPage.page.number + 1 < lastPage.page.totalPages ? lastPage.page.number + 1 : undefined,
  });

  // Sentinel element at the bottom of the grid — once it scrolls into view,
  // fetch the next page. Plain IntersectionObserver rather than a library:
  // this is the only infinite-scroll spot in the app so far, not worth a
  // new dependency for.
  const loadMoreRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const target = loadMoreRef.current;
    if (!target) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && featured.hasNextPage && !featured.isFetchingNextPage) {
          featured.fetchNextPage();
        }
      },
      { rootMargin: '400px' }, // start fetching a bit before the sentinel is actually on-screen
    );
    observer.observe(target);
    return () => observer.disconnect();
  }, [featured.hasNextPage, featured.isFetchingNextPage, featured.fetchNextPage]);

  const stats = useQuery({
    queryKey: ['public-stats'],
    queryFn: fetchPublicStats,
    staleTime: 60_000,
  });

  // Removed citiesQuery: the search bar is now a free-text input that hits
  // the backend's LIKE-based city search, so a master city list isn't needed.

  const handleSearch = (value: OtaSearchValue) => {
    const params = new URLSearchParams();
    // propertyId (a specific hotel picked from the suggestion dropdown) takes
    // precedence over q (plain typed text) — see PropertyRepository.search.
    if (value.propertyId) params.set('propertyId', String(value.propertyId));
    else if (value.query) params.set('q', value.query);
    if (value.checkIn) params.set('checkIn', value.checkIn);
    if (value.checkOut) params.set('checkOut', value.checkOut);
    if (value.guests) params.set('guests', String(value.guests));
    navigate(`/hotels?${params.toString()}`);
  };

  const handleCityClick = (city: string) => {
    navigate(`/hotels?city=${encodeURIComponent(city)}`);
  };

  const allProperties = featured.data?.pages.flatMap((pg) => pg.content) ?? [];
  const summaries: OtaPropertySummary[] = allProperties.map((p) => ({
    id: p.id,
    name: p.name,
    city: p.city,
    area: p.area,
    primaryImageUrl: p.imageIds?.[0] ? `/api/media/${p.imageIds[0]}` : undefined,
    imageUrls: (p.imageIds ?? []).map((imgId) => `/api/media/${imgId}`),
    startingPrice: p.startingPrice ?? 0,
    rating: p.avgRating,
    reviewCount: p.reviewCount,
    amenities: p.amenities.slice(0, 3).map((a) => a.label),
    starRating: p.starRating,
  }));

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <PublicHeader />

      {/* ===== HERO ===== */}
      <Box
        sx={{
          position: 'relative',
          overflow: 'hidden',
          color: '#fff',
          pt: { xs: 6, md: 12 },
          pb: { xs: 14, md: 22 },
          background: `
            radial-gradient(ellipse at 20% 0%, rgba(31, 141, 214, 0.45) 0%, transparent 50%),
            radial-gradient(ellipse at 80% 100%, rgba(235, 111, 3, 0.20) 0%, transparent 50%),
            linear-gradient(135deg, #022454 0%, #053C71 50%, #04356A 100%)
          `,
        }}
      >
        {/* Decorative diagonal pattern */}
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            inset: 0,
            backgroundImage: `
              radial-gradient(circle at 1px 1px, rgba(255,255,255,0.06) 1px, transparent 0)
            `,
            backgroundSize: '24px 24px',
            maskImage: 'linear-gradient(180deg, #000 0%, #000 60%, transparent 100%)',
            WebkitMaskImage: 'linear-gradient(180deg, #000 0%, #000 60%, transparent 100%)',
            pointerEvents: 'none',
          }}
        />
        {/* Floating decorative blobs */}
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            top: '-10%',
            right: '-5%',
            width: { xs: 240, md: 480 },
            height: { xs: 240, md: 480 },
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(235,111,3,0.25) 0%, transparent 70%)',
            filter: 'blur(40px)',
            pointerEvents: 'none',
          }}
        />

        <Container maxWidth="xl" sx={{ position: 'relative' }}>
          <Grid container alignItems="center" spacing={4}>
            <Grid item xs={12} md={8}>
              <Stack spacing={3} sx={{ maxWidth: 760 }}>
                <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
                  <Chip
                    icon={<StarIcon />}
                    label="Trusted by 50,000+ travelers"
                    sx={{
                      bgcolor: 'rgba(255,255,255,0.12)',
                      color: '#fff',
                      backdropFilter: 'blur(8px)',
                      border: '1px solid rgba(255,255,255,0.15)',
                      '& .MuiChip-icon': { color: 'secondary.light' },
                    }}
                  />
                  <Chip
                    label="Hotels · Stays · Resorts"
                    sx={{
                      bgcolor: 'rgba(255,255,255,0.12)',
                      color: '#fff',
                      backdropFilter: 'blur(8px)',
                      border: '1px solid rgba(255,255,255,0.15)',
                    }}
                  />
                </Stack>
                <Typography
                  variant="h1"
                  sx={{
                    color: '#fff',
                    fontWeight: 800,
                    fontSize: { xs: '2.5rem', sm: '3rem', md: '4rem' },
                    lineHeight: 1.05,
                    letterSpacing: '-0.02em',
                  }}
                >
                  Find your{' '}
                  <Box
                    component="span"
                    sx={{
                      background: 'linear-gradient(135deg, #FFD37A 0%, #EB6F03 100%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                    }}
                  >
                    perfect stay
                  </Box>
                  <br />
                  in seconds.
                </Typography>
                <Typography variant="h6" sx={{ color: '#D5E1F2', fontWeight: 400, maxWidth: 600 }}>
                  Real-time availability, transparent pricing, and bookings you can modify any time.
                  Built by Dataman, made for travelers.
                </Typography>
              </Stack>
            </Grid>

            {/* Right-side stats card on md+ */}
            <Grid item xs={12} md={4} sx={{ display: { xs: 'none', md: 'block' } }}>
              <Stack
                spacing={2}
                sx={{
                  p: 3,
                  borderRadius: 4,
                  bgcolor: 'rgba(255,255,255,0.08)',
                  border: '1px solid rgba(255,255,255,0.15)',
                  backdropFilter: 'blur(12px)',
                }}
              >
                <Stat
                  label="Verified properties"
                  value={stats.data ? stats.data.activeProperties.toLocaleString('en-IN') : '—'}
                />
                <Stat
                  label="Cities covered"
                  value={stats.data ? stats.data.citiesCovered.toLocaleString('en-IN') : '—'}
                />
              </Stack>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ===== FLOATING SEARCH BAR ===== */}
      <Container maxWidth="xl" sx={{ mt: { xs: -10, md: -12 }, position: 'relative', zIndex: 2 }}>
        <OtaSearchBar onSearch={handleSearch} onSuggest={fetchPropertySuggestions} />
      </Container>

      {/* ===== ALL PROPERTIES (infinite scroll) ===== */}
      <Container maxWidth="xl" sx={{ py: { xs: 6, md: 10 } }}>
        <Stack spacing={1} sx={{ mb: 4 }}>
          <Typography variant="overline" color="secondary.main">
            Trending stays
          </Typography>
          <Typography variant="h3">Explore all stays</Typography>
          <Typography variant="body1" color="text.secondary">
            {summaries.length === 0 && featured.isFetched
              ? 'No active properties yet. Once admins enlist hotels and the owners publish them, they appear here.'
              : 'Browse every property on OTA — keep scrolling for more.'}
          </Typography>
        </Stack>

        <Grid container spacing={3}>
          {summaries.map((p) => (
            <Grid key={p.id} item xs={12} sm={6} md={4} lg={3}>
              <OtaPropertyCard property={p} onClick={(id) => navigate(`/hotels/${id}`)} />
            </Grid>
          ))}
        </Grid>

        {/* Sentinel — IntersectionObserver above triggers fetchNextPage() when this
            scrolls into view. Kept mounted (not conditionally rendered) whenever
            there's more to load, so the observer always has something to watch. */}
        {featured.hasNextPage && (
          <Box ref={loadMoreRef} sx={{ display: 'flex', justifyContent: 'center', pt: 5 }}>
            <CircularProgress size={28} />
          </Box>
        )}
      </Container>

      {/* ===== POPULAR CITIES ===== */}
      <OtaPopularCities onCityClick={handleCityClick} />

      {/* ===== VALUE PROPS ===== */}
      <OtaValueProps />

      {/* ===== CTA STRIP ===== */}
      <Box
        sx={{
          py: { xs: 6, md: 8 },
          background: 'linear-gradient(135deg, #053C71 0%, #04356A 100%)',
          color: '#fff',
        }}
      >
        <Container maxWidth="md">
          <Stack alignItems="center" spacing={2} sx={{ textAlign: 'center' }}>
            <Typography variant="overline" sx={{ color: 'secondary.light' }}>
              Are you a hotelier?
            </Typography>
            <Typography variant="h3" sx={{ color: '#fff', maxWidth: 720 }}>
              List your property on OTA — reach travelers across India
            </Typography>
            <Typography variant="body1" sx={{ color: '#D5E1F2', maxWidth: 540 }}>
              Join thousands of properties already booking through OTA. Easy onboarding,
              owner-friendly dashboards, real-time inventory.
            </Typography>
            <Box
              component="a"
              href="https://otaadmin.dataman.in"
              sx={{
                mt: 1,
                display: 'inline-block',
                px: 4,
                py: 1.75,
                borderRadius: 2,
                background: 'linear-gradient(135deg, #EB6F03 0%, #FF9326 100%)',
                color: '#fff',
                fontWeight: 600,
                textDecoration: 'none',
                boxShadow: '0 8px 24px rgba(235, 111, 3, 0.40)',
                transition: 'transform 0.18s ease',
                '&:hover': { transform: 'translateY(-2px)' },
              }}
            >
              Enlist your property
            </Box>
          </Stack>
        </Container>
      </Box>

      <Box sx={{ flex: 1 }} />
      <OtaFooter />
    </Box>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <Stack direction="row" justifyContent="space-between" alignItems="baseline">
      <Typography variant="body2" sx={{ color: '#B5C2D2' }}>
        {label}
      </Typography>
      <Typography variant="h5" sx={{ color: '#fff', fontWeight: 700 }}>
        {value}
      </Typography>
    </Stack>
  );
}
